"use client";
import React, { useState, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { Button } from "@/components/ui/button";
import GetInTouch from "@/components/modals/get-in-touch";
import Image from "next/image";
import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";

const Hero = () => {
  const [isGetInTouchOpen, setIsGetInTouchOpen] = useState(false);
  const searchParams = useSearchParams();

  useEffect(() => {
    if (searchParams.get("contact") !== null) {
      setIsGetInTouchOpen(true);
    }
  }, [searchParams]);
  const t = useTranslations("hero");

  return (
    <section className="section pt-[156px] md:pt-[172px] lg:pt-[150px] xl:pt-[200px] pb-[56px] md:pb-[80px] relative">
      {/* BACKGROUND */}
      <div className="absolute top-0 left-0 w-full h-full z-0">
        <Image
          src="/assets/backgrounds/spine.webp"
          alt="Abstract technology background"
          aria-hidden="true"
          width={3260}
          height={2138}
          priority
          fetchPriority="high"
          sizes="100vw"
          className="absolute inset-0 w-full h-full object-cover mix-blend-multiply"
        />
      </div>

      {/* CONTENT */}
      <div className="container mx-auto relative z-10 space-y-[24px] md:space-y-[20px] pointer-events-none">
        <h1 className="text-[30px] sm:text-[38px] md:text-[55px] xl:text-[70px] font-semibold leading-[1.08] md:leading-[1.2] max-w-[320px] sm:max-w-[420px] md:max-w-[760px] lg:max-w-[900px]">
          {t("line1")} <br /> {t("line2")} <br /> {t("line3")}
        </h1>

        <div className="flex items-end justify-start md:justify-end md:pt-[12px] lg:pt-0">
          <div className="w-full max-w-[420px] md:max-w-[360px] lg:max-w-[390px] space-y-[24px] md:space-y-[20px] relative">
            {/* Glow behind content */}
            <div className="absolute -inset-x-[60px] -inset-y-[50px] bg-[radial-gradient(ellipse_at_center,_rgba(10,0,31,0.95)_30%,_transparent_75%)] pointer-events-none blur-[20px]" />
            <p className="relative max-w-[22rem] sm:max-w-[26rem] md:max-w-none lg:text-[18px] text-[16px] font-light leading-[1.55] md:leading-[1.6] text-foreground/90">
              {t("description")}
            </p>
            <div className="relative mt-[32px] md:mt-0 grid grid-cols-2 gap-[12px] pointer-events-auto w-full md:flex md:w-auto md:items-center md:gap-[10px]">
              <Button
                variant="theme"
                className="h-[48px] w-full min-w-0 md:w-auto md:min-w-[180px] rounded-[14px] px-[16px] md:px-[28px] text-[14px] md:text-[16px] shadow-none cursor-pointer"
                onClick={() => setIsGetInTouchOpen(true)}
              >
                {t("cta")}
              </Button>
              <Button
                variant="secondary"
                className="h-[48px] w-full min-w-0 md:w-auto md:min-w-[180px] rounded-[14px] px-[16px] md:px-[28px] text-[14px] md:text-[16px] shadow-none cursor-pointer"
                asChild
              >
                <Link href="/services">{t("secondary")}</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* OVERLAY */}
      <div className="absolute pointer-events-none h-[350px] w-full bottom-0 start-0 end-0 z-[1] bg-[linear-gradient(180deg,_rgba(10,_0,_31,_0)_0%,_#0A001F_100%)]" />

      <GetInTouch open={isGetInTouchOpen} onOpenChange={setIsGetInTouchOpen} />
    </section>
  );
};

export default Hero;
